import * as yup from "yup";
import { portugues } from "_utils/validador";
import { limparCpfCnpj, validarCNPJ, validarCPF } from "_utils/validarCpfCnpj";

yup.setLocale(portugues);

export const listarFornecedoresSchema = yup.object().shape({
  fornecedor: yup.string().nullable().optional(),
  cpfCnpj: yup.string().nullable().optional(),
  status: yup.string().nullable().optional(),
  uf: yup.string().nullable().optional(),
  cidade: yup.string().nullable().optional(),
  atividadesPrincipais: yup.array().of(yup.string().required()).nullable().optional(),
});

export const acaoFornecedorSchema = (
  tipoPessoa: "juridica" | "fisica",
  pix: boolean,
  transferencia: boolean
) => {
  const shape: any = {
    nomeRazaoSocial: yup
      .string()
      .required(
        tipoPessoa === "fisica"
          ? "Nome é obrigatório"
          : "Razão Social é obrigatória"
      ),
    nomeFantasia: yup
      .string()
      .nullable()
      .when([], {
        is: () => tipoPessoa === "juridica",
        then: (schema) => schema.required("Nome fantasia é obrigatório"),
        otherwise: (schema) => schema.optional(),
      }),
    telefone: yup
      .string()
      .nullable()
      .when([], {
        is: () => tipoPessoa === "fisica",
        then: (schema) =>
          schema
            .required("Telefone é obrigatório")
            .test("len", "Digite um telefone válido", (value) => {
              if (!value) return false;
              return value.trim().length === 14 || value.trim().length === 15;
            }),
        otherwise: (schema) => schema.optional(),
      }),
    email: yup
      .string()
      .nullable()
      .when([], {
        is: () => tipoPessoa === "fisica",
        then: (schema) =>
          schema
            .required("E-mail é obrigatório")
            .email("Digite um e-mail válido"),
        otherwise: (schema) => schema.optional(),
      }),
    cpfCnpj: yup
      .string()
      .required(
        tipoPessoa === "fisica" ? "CPF é obrigatório" : "CNPJ é obrigatório"
      )
      .test(
        "documento-valido",
        tipoPessoa === "fisica" ? "CPF inválido" : "CNPJ inválido",
        (value) => {
          if (!value) return false;

          const documentoLimpo = limparCpfCnpj(value);

          return tipoPessoa === "fisica"
            ? validarCPF(documentoLimpo)
            : validarCNPJ(documentoLimpo);
        }
      ),
    cep: yup.string().required("CEP é obrigatório"),
    endereco: yup.string().required("Endereço é obrigatório"),
    numero: yup.string().required("Número é obrigatório"),
    bairro: yup.string().required("Bairro é obrigatório"),
    cidade: yup.string().required("Cidade é obrigatória"),
    uf: yup.string().required("Estado é obrigatório"),
    complemento: yup.string().optional(),
    chavePix: yup.string().when([], {
      is: () => pix,
      then: (schema) => schema.required("Chave é obrigatória"),
      otherwise: (schema) => schema.optional(),
    }),
    titularContaPix: yup.string().when([], {
      is: () => pix,
      then: (schema) => schema.required("Titular é obrigatório"),
      otherwise: (schema) => schema.optional(),
    }),
    banco: yup.string().when([], {
      is: () => transferencia,
      then: (schema) => schema.required("Banco é obrigatório"),
      otherwise: (schema) => schema.optional(),
    }),
    agencia: yup.string().when([], {
      is: () => transferencia,
      then: (schema) =>
        schema
          .required("Agência é obrigatória")
          .matches(/^\d{4,5}$/, "Agência deve conter 4 ou 5 dígitos"),
      otherwise: (schema) => schema.optional(),
    }),
    conta: yup.string().when([], {
      is: () => transferencia,
      then: (schema) =>
        schema
          .required("Conta é obrigatória")
          .matches(/^\d{8,9}$/, "A conta deve conter 8 ou 9 dígitos"),
      otherwise: (schema) => schema.optional(),
    }),
    titularContaTransferencia: yup.string().when([], {
      is: () => transferencia,
      then: (schema) => schema.required("Titular é obrigatório"),
      otherwise: (schema) => schema.optional(),
    }),
    cpfCnpjTitularContaTransferencia: yup
      .string()
      .nullable()
      .when([], {
        is: () => transferencia,
        then: (schema) =>
          schema
            .required("Documento é obrigatório")
            .test("documento-valido", "Documento inválido", (value) => {
              if (!value) return false;
              const documentoLimpo = limparCpfCnpj(value);

              if (documentoLimpo.length === 11) {
                return validarCPF(documentoLimpo);
              } else if (documentoLimpo.length === 14) {
                return validarCNPJ(documentoLimpo);
              }

              return false;
            }),
        otherwise: (schema) => schema.optional().nullable(),
      }),
    responsaveis: yup.array().of(
      yup.object().shape({
        nomeResponsavel: yup.string().when([], {
          is: () => tipoPessoa === "juridica",
          then: (schema) => schema.required("Nome é obrigatório"),
          otherwise: (schema) => schema.optional(),
        }),
        telefone: yup.string().when([], {
          is: () => tipoPessoa === "juridica",
          then: (schema) =>
            schema
              .required("Telefone é obrigatório")
              .test("len", "Digite um telefone válido", (value) => {
                if (!value) return false;
                return value.trim().length === 14 || value.trim().length === 15;
              }),
          otherwise: (schema) => schema.optional(),
        }),
        email: yup.string().when([], {
          is: () => tipoPessoa === "juridica",
          then: (schema) =>
            schema
              .required("E-mail é obrigatório")
              .email("Digite um e-mail válido"),
          otherwise: (schema) => schema.optional(),
        }),
      })
    ),
  };

  return yup.object().shape(shape);
};
